import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getMe, setDayWindow, ApiError } from '../api';
import { Select } from './Select';

/**
 * The hours the day view draws.
 *
 * Deliberately not the same as working hours (§5.6), which decide when the planner may
 * *propose* work. Wanting to see a 06:00 gym session is not the same as wanting to be
 * offered one, and one setting for both would force a choice between the two.
 */
export function DayWindowSetting() {
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: getMe });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: ({ start, end }: { start: number; end: number }) => setDayWindow(start, end),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not save that.'),
  });

  const current = me.data?.dayWindow;
  if (!current) return null;

  const hours = Array.from({ length: 25 }, (_, h) => h);
  const label = (h: number) => `${String(h).padStart(2, '0')}:00`;

  return (
    <section className="card">
      <h3>Day view</h3>
      <p className="muted">
        The hours the timeline shows. Anything scheduled outside them still appears — the view
        widens rather than hiding it.
      </p>

      <div className="row gap">
        <label className="subtext">
          From
          <Select
            options={hours.slice(0, 23).map((h) => ({ value: String(h), label: label(h) }))}
            value={String(current.startHour)}
            onChange={(next) => next && save.mutate({ start: Number(next), end: current.endHour })}
            ariaLabel="Day starts at"
            allowEmpty={false}
            compact
          />
        </label>

        <label className="subtext">
          To
          <Select
            options={hours.slice(1).map((h) => ({ value: String(h), label: label(h) }))}
            value={String(current.endHour)}
            onChange={(next) =>
              next && save.mutate({ start: current.startHour, end: Number(next) })
            }
            ariaLabel="Day ends at"
            allowEmpty={false}
            compact
          />
        </label>
      </div>

      {error && <p className="error">{error}</p>}
    </section>
  );
}
